import { useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";

/**
 * <video> wrapper that detects the HEVC-on-desktop-Chrome black-screen bug:
 * audio plays fine but video frames don't render (Chrome can't decode HEVC).
 * We listen for `loadedmetadata` and inspect videoWidth/videoHeight — if both
 * are 0 OR if `mediaError` fires, we overlay a friendly note explaining the
 * limitation and offering a download link so the viewer can still see it.
 *
 * No new requests, no listeners that survive unmount — credit-efficient.
 */
export default function PostVideo({ src, contentType, fileId }) {
  const videoRef = useRef(null);
  const [problem, setProblem] = useState(null); // null | "unplayable"

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const checkDecoder = () => {
      // After metadata loads, a decode-capable browser populates these.
      // iPhone HEVC on desktop Chrome leaves both at 0.
      if (!v.videoWidth && !v.videoHeight) {
        setProblem("unplayable");
      }
    };
    const onError = () => setProblem("unplayable");
    v.addEventListener("loadedmetadata", checkDecoder);
    v.addEventListener("error", onError);
    return () => {
      v.removeEventListener("loadedmetadata", checkDecoder);
      v.removeEventListener("error", onError);
    };
  }, [src]);

  return (
    <div
      className="csc-video-frame rounded-md overflow-hidden border border-[rgba(0,170,255,0.18)] relative"
      data-testid={`post-video-${fileId}`}
    >
      <video
        ref={videoRef}
        controls
        preload="metadata"
        playsInline
        controlsList="nodownload"
        style={{ objectFit: "contain", background: "#000" }}
      >
        {/* Intentionally omit the `type` attribute so the browser uses the
            response's real Content-Type rather than the optimistic
            "video/mp4" hint we used to send — which mis-led Chrome into
            attempting an HEVC decode it couldn't complete. */}
        <source src={src} />
        Your browser doesn&apos;t support embedded video.
      </video>
      {problem === "unplayable" && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 py-3 bg-[#050d1a]/85 backdrop-blur-sm"
          data-testid={`video-decode-warning-${fileId}`}
        >
          <AlertTriangle size={22} className="text-[#f59e0b] mb-2" />
          <div className="text-sm text-white font-medium mb-1">Video can&apos;t play on this browser</div>
          <div className="text-xs text-[#94a3b8] max-w-md leading-relaxed">
            This clip was probably recorded on iPhone (HEVC / H.265). Try Safari, Microsoft Edge,
            or download the file to view it locally.
          </div>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 csc-btn-ghost !text-xs"
            data-testid={`video-download-${fileId}`}
          >
            Open / Download video
          </a>
          {contentType && (
            <div className="mt-2 font-mono-tech text-[10px] text-[#94a3b8]">{contentType}</div>
          )}
        </div>
      )}
    </div>
  );
}
